import { memo } from 'react';

import styled from '@emotion/styled';
import { Box, Divider, Typography } from '@kc-console/common-ui';
import { getCustomFormatDate, getSanitizedHtml } from '@kc-console/core';

import AnnouncementHeartButton from './AnnouncementHeartButton';
import { DATE_FORMAT } from './utils';

import type { BadgeColor } from '@kc-console/common-ui';
import type { AnnouncementContentItem } from './types';

/**
 * 카테고리(subTitle) Badge 색을 동일 계열의 텍스트 색 토큰으로 매핑합니다.
 * @description
 * - 위젯의 Badge 색(ANNOUNCEMENT_BADGE_COLORS)과 동일한 계열을 유지합니다.
 * - lightGreen 은 Badge 와 동일하게 teal 토큰을 사용합니다.
 */
const CATEGORY_TEXT_COLOR = {
  green: 'decorativeText/weak/green',
  yellow: 'decorativeText/weak/yellow',
  blue: 'decorativeText/weak/blue',
  purple: 'decorativeText/weak/purple',
  magenta: 'decorativeText/weak/magenta',
  sky: 'decorativeText/weak/sky',
  lightGreen: 'decorativeText/weak/teal',
  orange: 'decorativeText/weak/orange',
} as const;

const DEFAULT_CATEGORY_TEXT_COLOR = 'decorativeText/weak/purple';

interface AnnouncementContentAreaProps {
  /** 현재 선택된 새소식 (상세 API 응답) */
  announcement: AnnouncementContentItem;
  /** 카테고리(subTitle) Badge 색상 */
  badgeColor: BadgeColor | undefined;
}

/**
 * 선택된 새소식의 카테고리·제목·게시일과 본문(HTML)을 표시합니다.
 * @description
 * - 스크롤은 상위 모달의 스크롤 영역이 담당하며, 글이 짧으면 남는 공간을 이 영역이 채웁니다.
 */
const AnnouncementContentArea = ({ announcement, badgeColor }: AnnouncementContentAreaProps) => {
  const categoryColor =
    badgeColor && badgeColor in CATEGORY_TEXT_COLOR
      ? CATEGORY_TEXT_COLOR[badgeColor as keyof typeof CATEGORY_TEXT_COLOR]
      : DEFAULT_CATEGORY_TEXT_COLOR;

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="16px"
        marginBottom="16px"
        paddingRight="8px"
      >
        <Box display="flex" flexDirection="column" gap="4px" alignItems="flex-start">
          <Typography variant="label/medium/2xs" color={categoryColor}>
            {announcement.subTitle}
          </Typography>
          <Typography variant="heading/semibold/xl" color="text/default">
            {announcement.title}
          </Typography>
          <Typography variant="label/regular/xs" color="text/weakest/default">
            {announcement.publishedAt && getCustomFormatDate(announcement.publishedAt, DATE_FORMAT)}
          </Typography>
        </Box>
        {/*
          가운데 정렬 기준에서 8px 만 아래로 내려 놓습니다.
          패딩은 가운데 정렬 계산에 포함되므로, 위쪽 16px 이 절반인 8px 만큼의 하강으로 나타납니다.
        */}
        <Box paddingTop="16px">
          {/* 새소식이 바뀌면 하트 상태를 새로 시작하도록 key 로 재마운트합니다. */}
          <AnnouncementHeartButton key={announcement.id} announcementId={announcement.id} />
        </Box>
      </Box>
      <Box marginBottom="32px">
        <Divider />
      </Box>
      <HtmlContent
        dangerouslySetInnerHTML={{ __html: getSanitizedHtml(announcement.contentHtml) }}
      />
    </Box>
  );
};

// 목록 페이지 이동 시 본문 재-sanitize 등 불필요한 재렌더를 막기 위해 memo 로 감쌉니다.
export default memo(AnnouncementContentArea);

const HtmlContent = styled('div')`
  font-weight: 400;
  font-size: 16px;
  line-height: 160%;
  letter-spacing: 0%;
  text-decoration: none;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: revert;
    padding: revert;
    font-size: revert;
  }

  ol,
  p,
  ul {
    margin: 0;
    padding: revert;
  }

  u {
    text-decoration: underline;
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }

  table {
    width: 100% !important;
    border-collapse: collapse;
  }

  table td,
  table th {
    border: 1px solid #b3b3b3;
    padding: 5px 10px;
  }

  table th {
    background-color: rgb(245, 245, 245);
  }

  a {
    color: #16161b !important;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }

  strong {
    font-weight: bolder;
  }
  img {
    max-width: 100%;
  }
`;
